<template>
  <!-- Resolving, closing or re-opening a ticket, with a remark that goes into
       the conversation with the move (PUT /ticket/:id/status). The remark is
       optional; the wording is for whoever is moving it — Screenkode, or the
       organization. Over the ticket's own dialog. The same component lives in
       the admin and in both organization apps. -->
  <div class="tk-modal">
    <div
      ref="dialog"
      class="modal dn-modal dn-modal--stacked show"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ticket-status-title"
      @keydown.esc.stop="close()"
    >
      <div class="modal-dialog dn-modal-dialog dn-modal-dialog--sm">
        <div class="modal-content dn-modal-content">
          <div class="dn-modal-header">
            <div class="dn-modal-heading">
              <span class="dn-modal-glyph tsm-glyph" :class="`tsm-glyph--${move}`">
                <i class="pi" :class="copy.icon"></i>
              </span>
              <div class="dn-modal-heading-text">
                <h5 id="ticket-status-title" class="dn-modal-title">{{ copy.title }}</h5>
                <p class="dn-modal-subtitle">{{ copy.subtitle }}</p>
              </div>
            </div>
            <button type="button" class="dn-modal-close" aria-label="Close" @click="close()">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <form class="dn-modal-form" @submit.prevent="submit">
            <div class="dn-modal-body">
              <div class="dn-form-group">
                <label for="ticket-status-remark">
                  {{ copy.label }} <span class="tsm-optional">(optional)</span>
                </label>
                <textarea
                  id="ticket-status-remark"
                  ref="remark"
                  v-model="comment"
                  class="form-control"
                  rows="4"
                  :maxlength="maxLength"
                  :placeholder="copy.placeholder"
                  :disabled="busy"
                  @keydown.enter.meta.prevent="submit"
                  @keydown.enter.ctrl.prevent="submit"
                ></textarea>
                <span class="tsm-hint">{{ copy.hint }}</span>
              </div>
            </div>

            <div class="dn-modal-footer">
              <button type="button" class="btn btn-custom-secondary" :disabled="busy" @click="close()">
                Cancel
              </button>
              <button type="submit" class="btn btn-custom-primary tsm-submit" :class="`tsm-submit--${move}`" :disabled="busy">
                <span v-if="busy" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <template v-else>
                  <i class="pi" :class="copy.icon" aria-hidden="true"></i>
                  <span>{{ copy.submit }}</span>
                </template>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { SET_TICKET_STATUS } from '@/store/action_types';
import { TICKET_SIDE_STAFF } from '@/utils/ticket';
import { TICKET_MOVE_CLOSED, TICKET_MOVE_REOPENED, TICKET_MOVE_RESOLVED } from '@/utils/ticket_lifecycle';

// The longest remark the API takes with a move.
const REMARK_MAX_LENGTH = 5000;

export default {
  name: 'TicketStatusModal',
  props: {
    ticketId: {
      type: String,
      required: true,
    },
    // resolved, closed or reopened.
    move: {
      type: String,
      required: true,
      validator: (value) => [TICKET_MOVE_RESOLVED, TICKET_MOVE_CLOSED, TICKET_MOVE_REOPENED].includes(value),
    },
    // Who is moving it: staff, or the organization.
    viewerSide: {
      type: String,
      default: TICKET_SIDE_STAFF,
    },
    // The support team, as the organization's app names it.
    teamName: {
      type: String,
      default: 'Screenkode',
    },
  },
  emits: ['close', 'done'],
  data() {
    return {
      comment: '',
      busy: false,
      maxLength: REMARK_MAX_LENGTH,
    };
  },
  computed: {
    isStaff() {
      return this.viewerSide === TICKET_SIDE_STAFF;
    },
    copy() {
      if (this.move === TICKET_MOVE_RESOLVED) {
        return {
          icon: 'pi-check-circle',
          title: 'Resolve ticket',
          subtitle: 'They will be asked to confirm it is fixed — or to re-open it.',
          label: 'How it was resolved',
          placeholder: 'What did you change? Is there anything they should check?',
          hint: 'Shown in the conversation, and in the email they get.',
          submit: 'Mark resolved',
        };
      }
      if (this.move === TICKET_MOVE_REOPENED) {
        return {
          icon: 'pi-lock-open',
          title: 'Re-open ticket',
          subtitle: this.isStaff
            ? 'It goes back to whoever looks after it.'
            : `Tell ${this.teamName} what is still not working, and they will take another look.`,
          label: this.isStaff ? 'Remark' : 'What is still wrong?',
          placeholder: this.isStaff ? 'Why it is being re-opened…' : 'Describe what is still not working…',
          hint: 'Shown in the conversation.',
          submit: 'Re-open ticket',
        };
      }
      return {
        icon: 'pi-lock',
        title: 'Close ticket',
        subtitle: this.isStaff
          ? 'The conversation ends here. They can re-open it if they need to.'
          : 'Close it once everything is working. You can re-open it later.',
        label: 'Remark',
        placeholder: this.isStaff ? 'Anything to note before it closes…' : 'Anything to add before you close it…',
        hint: 'Shown in the conversation.',
        submit: 'Close ticket',
      };
    },
  },
  methods: {
    close() {
      if (!this.busy) this.$emit('close');
    },
    async submit() {
      if (this.busy) return;
      this.busy = true;
      let ticket = null;
      try {
        ticket = await this.$store.dispatch(SET_TICKET_STATUS, {
          id: this.ticketId,
          to: this.move,
          comment: this.comment.trim() || undefined,
        });
      } finally {
        this.busy = false;
      }
      if (!ticket) return;
      this.$emit('done', ticket);
      this.$emit('close');
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.remark?.focus());
  },
};
</script>
<style scoped>
.tsm-optional {
  color: var(--color-text-muted);
  font-weight: 400;
}

.tsm-hint {
  display: block;
  margin-top: 6px;
  color: var(--color-text-muted);
  font-size: 0.72rem;
}

/* The glyph and the button in the move's own colour. */
.tsm-glyph--resolved {
  background: #E8F8F0 !important;
  color: #0E9F6E !important;
}

.tsm-glyph--closed {
  background: #FEECEC !important;
  color: var(--color-danger, #C81E1E) !important;
}

.tsm-glyph--reopened {
  background: var(--color-primary-soft) !important;
  color: var(--color-primary-hover) !important;
}

.tsm-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 132px;
}

.tsm-submit .pi {
  margin: 0;
  font-size: 0.78rem;
}

.btn.tsm-submit--resolved,
.btn.tsm-submit--resolved:hover:not(:disabled) {
  border-color: #0E9F6E !important;
  background: #0E9F6E !important;
}

.btn.tsm-submit--resolved:hover:not(:disabled) {
  filter: brightness(0.94);
}

.btn.tsm-submit--closed,
.btn.tsm-submit--closed:hover:not(:disabled) {
  border-color: var(--color-danger, #C81E1E) !important;
  background: var(--color-danger, #C81E1E) !important;
}

.btn.tsm-submit--closed:hover:not(:disabled) {
  filter: brightness(0.94);
}
</style>
